import React, { useState } from "react";
import { ConfirmedTeamDataFResults } from "../../../../../types/team.types";
import { useToast } from "../../../../../components/toast";
import { CompetitionsAPI } from "../../../../../APIs/competitions.api";
import { Button } from "../../../../../components/button";
import { EditableField } from "../../../../../components/editable-field";

interface CreateCategoryResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  competitionId: number;
  teams: ConfirmedTeamDataFResults[];
  categories: Array<{ id: number; name: string }>;
}

interface FormData {
  team_id: string;
  category_id: string;
  points: string;
  species_count: string;
  pieces_count: string;
}

export function CreateCategoryResultModal({
  isOpen,
  onClose,
  onSuccess,
  competitionId,
  teams,
  categories,
}: CreateCategoryResultModalProps) {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    team_id: "",
    category_id: "",
    points: "",
    species_count: "",
    pieces_count: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.team_id || !formData.category_id) {
      addToast({
        type: "error",
        message: "Por favor selecciona un equipo y una categoría",
        location: "bottom-center",
      });
      return;
    }

    try {
      setLoading(true);

      const response = await CompetitionsAPI.postTopCategoryResult({
        competition_id: competitionId,
        team_id: parseInt(formData.team_id),
        category_id: parseInt(formData.category_id),
        points: parseInt(formData.points),
        species_count: parseInt(formData.species_count),
        pieces_count: parseInt(formData.pieces_count),
      });

      if (response.statusCode === 201 || response.statusCode === 200) {
        onSuccess();
        resetForm();
      } else {
        throw new Error(response.message || "Error al crear el resultado");
      }
    } catch (error) {
      addToast({
        type: "error",
        message: "Error al crear el resultado de categoría",
        location: "bottom-center",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      team_id: "",
      category_id: "",
      points: "",
      species_count: "",
      pieces_count: "",
    });
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Agregar Resultado de Categoría
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Team Selection */}
          <EditableField
            label="Equipo"
            value={formData.team_id}
            name="team_id"
            type="select"
            onChange={handleInputChange}
            required
            disabled={loading}
            placeholder="Seleccionar equipo"
            options={teams.map((team) => ({
              key: team.id.toString(),
              value: `${team.name}${
                team.institution?.name ? ` - ${team.institution.name}` : ""
              }`,
            }))}
          />

          {/* Category Selection */}
          <EditableField
            label="Categoría"
            value={formData.category_id}
            name="category_id"
            type="select"
            onChange={handleInputChange}
            required
            disabled={loading}
            placeholder="Seleccionar categoría"
            options={categories.map((category) => ({
              key: category.id.toString(),
              value: category.name,
            }))}
          />
          {categories.length === 0 && (
            <p className="text-sm text-gray-500 mt-1">
              Todas las categorías ya tienen resultados asignados
            </p>
          )}

          {/* Points */}
          <EditableField
            label="Puntos"
            value={formData.points}
            name="points"
            type="number"
            onChange={handleInputChange}
            required
            disabled={loading}
            min={0}
            placeholder="Ej: 120"
          />

          {/* Species Count */}
          <EditableField
            label="Cantidad de Especies"
            value={formData.species_count}
            name="species_count"
            type="number"
            onChange={handleInputChange}
            required
            disabled={loading}
            min={0}
            placeholder="Ej: 4"
          />

          {/* Pieces Count */}
          <EditableField
            label="Cantidad de Piezas"
            value={formData.pieces_count}
            name="pieces_count"
            type="number"
            onChange={handleInputChange}
            required
            disabled={loading}
            min={0}
            placeholder="Ej: 8"
          />

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              text="Cancelar"
              onClick={handleClose}
              variant="cancel"
              disabled={loading}
            />
            <Button
              type="submit"
              text={loading ? "Creando..." : "Crear Resultado"}
              variant="create"
              disabled={loading || categories.length === 0}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
